import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container, Grid, useTheme, useMediaQuery, InputAdornment, TextField, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import SubAgentRecordsTable from '../components/SubAgentRecordsTable';
import SubAgentUserFormDrawer from '../components/SubAgentUserFormDrawer';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import PasswordResetHistoryDrawer from '../components/PasswordResetHistoryDrawer';
import { useLocation } from 'react-router-dom';
import { useToast } from '../components/ui/ToastProvider';
import { logUserCreation, logUserEdit, logStatusChange, logPasswordReset } from '../utils/auditLog';
import { generateSecureOTP, hashPassword } from '../utils/passwordPolicy';
import { sendNewUserOTPEmail, sendPasswordResetEmail } from '../utils/emailService';
import { createPasswordResetHistory } from '../utils/passwordResetHistory';
import { shouldShowResendEmail, logEmailResend } from '../utils/passwordResetHistory';
interface SubAgentUsersProps {
  onLogout: () => void;
}
const STORAGE_KEY = 'sub_agent_users';
const SUB_AGENTS = ['Paga Ghana', 'MoneyGram Nigeria', 'WorldRemit Kenya', 'Flutterwave Ghana', 'Flutterwave Nigeria', 'M-Pesa Kenya', 'Orange Money Senegal', 'Accra Money Center', 'Lagos Express', 'Nairobi Remit Center'];
const BRANCH_CODES = ['GH-ACC-001', 'GH-KUM-001', 'NG-LAG-001', 'NG-ABJ-001', 'SN-DKR-001', 'UG-KLA-001', 'KE-NBI-001', 'CI-ABJ-001'];
// Initial mock data
const INITIAL_MOCK_USERS = Array(30).fill(null).map((_, index) => ({
  id: index + 1,
  name: ['Kwame Asante', 'Fatima Diallo', 'Chinedu Obi', 'Aisha Nakamura', 'David Kamau'][index % 5],
  fatherName: ['Operations', 'Compliance', 'Treasury', 'KYC/AML', 'Settlement'][index % 5],
  cnic: index % 3 === 0 ? ['GH', 'NG', 'SN', 'UG', 'KE'][index % 5] + (1000000 + index * 3) : '',
  email: `${['kwame.asante', 'fatima.diallo', 'chinedu.obi', 'aisha.nakamura', 'david.kamau'][index % 5]}${index}@ecobank.${['com.gh', 'com.ng', 'sn', 'ug', 'ke'][index % 5]}`,
  phone: [`+233 20 ${1000000 + index}`, `+234 81 ${1000000 + index}`, `+221 77 ${1000000 + index}`, `+256 70 ${1000000 + index}`, `+254 722 ${100000 + index}`][index % 5],
  username: ['kwame.asante', 'fatima.diallo', 'chinedu.obi', 'aisha.nakamura', 'david.kamau'][index % 5] + index,
  branchCode: BRANCH_CODES[index % BRANCH_CODES.length],
  settlementAccount: `ECO-BR-${10000000 + index * 7}`,
  subAgent: SUB_AGENTS[index % SUB_AGENTS.length],
  status: index % 7 === 0 ? 'inactive' : 'active',
  userType: index % 3 === 0 ? 'checker' : 'maker',
  createdAt: new Date(Date.now() - Math.floor(Math.random() * 10000000000)).toISOString()
}));
const SubAgentUsers: React.FC<SubAgentUsersProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDrawerOpen, setUserDrawerOpen] = useState(false);
  const [editUser, setEditUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [subAgentFilter, setSubAgentFilter] = useState<string | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUserForReset, setSelectedUserForReset] = useState<any>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | undefined>();
  const [resetHistoryDrawerOpen, setResetHistoryDrawerOpen] = useState(false);
  const [selectedUserForResetHistory, setSelectedUserForResetHistory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const {
    showToast
  } = useToast();
  // Load users from localStorage on mount
  useEffect(() => {
    const savedUsers = localStorage.getItem(STORAGE_KEY);
    if (savedUsers) {
      try {
        const parsedUsers = JSON.parse(savedUsers);
        setUsers(parsedUsers);
      } catch (error) {
        console.error('Error loading sub agent users from localStorage:', error);
        setUsers(INITIAL_MOCK_USERS);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_MOCK_USERS));
      }
    } else {
      // First time load - use mock data
      setUsers(INITIAL_MOCK_USERS);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_MOCK_USERS));
    }
  }, []);
  // Save users to localStorage whenever they change
  useEffect(() => {
    // Always save to localStorage, even if empty
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
    } catch (error) {
      console.error('Error saving users to localStorage:', error);
      showToast('Failed to save user data', 'error');
    }
  }, [users]);
  useEffect(() => {
    // Check for subAgent filter in URL
    const params = new URLSearchParams(location.search);
    const subAgentParam = params.get('subAgent');
    if (subAgentParam) {
      setSubAgentFilter(subAgentParam);
    }
  }, [location]);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleAddUser = () => {
    setEditUser(null);
    setUserDrawerOpen(true);
  };
  const handleEditUser = (user: any) => {
    setEditUser(user);
    setUserDrawerOpen(true);
  };
  const handleCloseUserDrawer = () => {
    setUserDrawerOpen(false);
    setEditUser(null);
  };
  const handleSaveUser = async (userData: any) => {
    if (!userData.id) {
      // New user creation
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      const newUser = {
        ...userData,
        id: users.length > 0 ? Math.max(...users.map(u => u.id)) + 1 : 1,
        status: 'active' as const,
        createdAt: new Date().toISOString(),
        hashedPassword: hashedOTP,
        firstLoginRequired: true,
        passwordResetRequired: true
      };
      const updatedUsers = [newUser, ...users];
      setUsers(updatedUsers);
      logUserCreation(newUser.id.toString(), newUser.name, newUser);
      try {
        showToast('Sending OTP email...', 'info', 'Creating User', 2000);
        const emailResult = await sendNewUserOTPEmail({
          recipientEmail: newUser.email,
          recipientName: newUser.name,
          otp: newOTP,
          expiryMinutes: 24
        });
        createPasswordResetHistory(newUser.id.toString(), newUser.name, 'SYSTEM', newUser.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
          resetByName: 'System (User Creation)',
          reason: 'New user account created',
          deliveryError: emailResult.error,
          emailMessageId: emailResult.messageId
        });
        if (emailResult.success) {
          setUserDrawerOpen(false);
          showToast(`User created successfully. Temporary password sent to ${newUser.email}`, 'success', 'User Created', 6000);
        } else {
          setUserDrawerOpen(false);
          showToast(`User created but email delivery failed: ${emailResult.error}. Temporary password: ${newOTP}`, 'error', 'Email Delivery Failed', 10000);
        }
      } catch (error) {
        createPasswordResetHistory(newUser.id.toString(), newUser.name, 'SYSTEM', newUser.email, 'FAILED', {
          resetByName: 'System (User Creation)',
          reason: 'New user account created',
          deliveryError: 'Email service error'
        });
        setUserDrawerOpen(false);
        showToast(`User created but email failed to send. Temporary password: ${newOTP}`, 'error', 'Email Service Error', 10000);
      }
    } else {
      // User update
      const oldUser = users.find(u => u.id === userData.id);
      const updatedUsers = users.map(u => u.id === userData.id ? {
        ...userData,
        createdAt: u.createdAt
      } : u);
      setUsers(updatedUsers);
      // Log user edit in audit log
      if (oldUser) {
        logUserEdit(userData.id.toString(), userData.name, oldUser, userData);
      }
      setUserDrawerOpen(false);
      showToast('User updated successfully', 'success');
    }
  };
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  const clearSearch = () => {
    setSearchQuery('');
  };
  const handleResetPassword = (user: any) => {
    setSelectedUserForReset(user);
    setResetPasswordDialogOpen(true);
    setResetPasswordError(undefined);
  };
  const confirmResetPassword = async (reason?: string) => {
    if (!selectedUserForReset) return;
    setIsResettingPassword(true);
    setResetPasswordError(undefined);
    try {
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      const updatedUsers = users.map(u => u.id === selectedUserForReset.id ? {
        ...u,
        hashedPassword: hashedOTP,
        passwordResetRequired: true,
        lastPasswordReset: new Date().toISOString()
      } : u);
      setUsers(updatedUsers);
      logPasswordReset(selectedUserForReset.id.toString(), selectedUserForReset.name, reason);
      const emailResult = await sendPasswordResetEmail({
        recipientEmail: selectedUserForReset.email,
        recipientName: selectedUserForReset.name,
        otp: newOTP,
        resetBy: 'BO Admin',
        reason: reason
      });
      createPasswordResetHistory(selectedUserForReset.id.toString(), selectedUserForReset.name, 'BO_ADMIN', selectedUserForReset.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
        resetByName: 'Admin User',
        reason: reason || 'Password reset requested by admin',
        deliveryError: emailResult.error,
        emailMessageId: emailResult.messageId
      });
      setResetPasswordDialogOpen(false);
      setIsResettingPassword(false);
      setSelectedUserForReset(null);
      if (emailResult.success) {
        showToast(`Password reset successful. New temporary password sent to ${selectedUserForReset.email}`, 'success', 'Password Reset', 6000);
      } else {
        showToast(`Password reset successful but email delivery failed: ${emailResult.error}. New temporary password: ${newOTP}`, 'warning', 'Password Reset - Email Failed', 10000);
      }
    } catch (error) {
      setResetPasswordError('Failed to reset password. Please try again.');
      setIsResettingPassword(false);
    }
  };
  const handleViewResetHistory = (user: any) => {
    setSelectedUserForResetHistory({
      id: user.id.toString(),
      name: user.name
    });
    setResetHistoryDrawerOpen(true);
  };
  const handleResendEmail = async (user: any) => {
    try {
      showToast('Sending OTP email...', 'info', 'Resending Email', 2000);
      // Generate new secure OTP
      const newOTP = generateSecureOTP();
      const hashedOTP = hashPassword(newOTP);
      // Update user with new hashed password
      const updatedUsers = users.map(u => u.id === user.id ? {
        ...u,
        hashedPassword: hashedOTP,
        passwordResetRequired: true,
        lastPasswordReset: new Date().toISOString()
      } : u);
      setUsers(updatedUsers);
      // Send OTP email
      const emailResult = await sendPasswordResetEmail({
        recipientEmail: user.email,
        recipientName: user.name,
        otp: newOTP,
        resetBy: 'BO Admin',
        reason: 'Email resend requested by admin'
      });
      // Create password reset history entry
      createPasswordResetHistory(user.id.toString(), user.name, 'BO_ADMIN', user.email, emailResult.success ? 'SUCCESS' : 'FAILED', {
        resetByName: 'Admin User',
        reason: 'Email resend - OTP regenerated',
        deliveryError: emailResult.error,
        emailMessageId: emailResult.messageId
      });
      // Log email resend in audit log
      logEmailResend(user.id.toString(), user.name, user.email, emailResult.success, 'Email resend requested by admin');
      // Show appropriate toast based on email delivery
      if (emailResult.success) {
        showToast(`Email sent successfully to ${user.email}`, 'success', 'Email Sent', 6000);
      } else {
        showToast(`Email sending failed: ${emailResult.error}. New temporary password: ${newOTP}`, 'error', 'Email Failed', 10000);
      }
    } catch (error) {
      showToast('Failed to resend email. Please try again.', 'error', 'Error', 6000);
    }
  };
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: '#F8FAFC'
  }} data-id="element-5041">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-5042" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="sub-agent-users" data-id="element-5043" />
      <Box component="main" sx={{
      flexGrow: 1,
      py: 2,
      px: 1,
      width: {
        xs: '100%',
        sm: `calc(100% - ${240}px)`
      },
      ml: {
        xs: 0,
        sm: 0
      },
      mt: 8
    }} data-id="element-5044">
        <Container maxWidth="xl" data-id="element-5045">
          {/* Header */}
          <Grid container spacing={2} alignItems="center" sx={{
          mb: 3
        }} data-id="element-5046">
            <Grid item xs={12} md={6} data-id="element-5047">
              <Box sx={{
              display: 'flex',
              alignItems: 'center'
            }} data-id="element-5048">
                <Icon icon="lucide:user" width={24} height={24} color="#0EA5E9" data-id="element-5049" />
                <Typography variant="h5" component="h1" sx={{
                ml: 1.5,
                fontWeight: 600
              }} data-id="element-5050">
                  Sub Agent Users
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6} data-id="element-5051">
              <Box sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              justifyContent: {
                xs: 'flex-start',
                md: 'flex-end'
              }
            }} data-id="element-5052">
                <Button variant="contained" startIcon={<Icon icon="lucide:plus" width={18} height={18} data-id="element-5054" />} onClick={handleAddUser} data-id="element-5053">
                  Add User
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} data-id="element-5055">
              <TextField fullWidth placeholder="Search users by name, email, passport, or phone..." variant="outlined" value={searchQuery} onChange={handleSearch} InputProps={{
              startAdornment: <InputAdornment position="start" data-id="element-5057">
                      <Icon icon="lucide:search" width={20} height={20} color="#475569" data-id="element-5058" />
                    </InputAdornment>,
              endAdornment: searchQuery ? <InputAdornment position="end" data-id="element-5059">
                      <IconButton size="small" onClick={clearSearch} data-id="element-5060">
                        <Icon icon="lucide:x" width={18} height={18} data-id="element-5061" />
                      </IconButton>
                    </InputAdornment> : null
            }} data-id="element-5056" />
            </Grid>
          </Grid>
          {/* Sub Agent Users Table */}
          <SubAgentRecordsTable records={users} onEditRecord={handleEditUser} searchQuery={searchQuery} subAgentFilter={subAgentFilter} onStatusChange={(user, newStatus) => {
          const updatedUsers = users.map(u => u.id === user.id ? {
            ...u,
            status: newStatus
          } : u);
          setUsers(updatedUsers);
          logStatusChange(user.id.toString(), user.name, user.status, newStatus);
          showToast(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`, 'success');
        }} onResetPassword={handleResetPassword} onViewResetHistory={handleViewResetHistory} onResendEmail={handleResendEmail} data-id="element-5062" />
          {/* Sub Agent User Form Drawer */}
          <SubAgentUserFormDrawer open={userDrawerOpen} user={editUser} onClose={handleCloseUserDrawer} onSave={handleSaveUser} preselectedSubAgent={subAgentFilter} data-id="element-5063" />
          {/* Reset Password Confirmation Dialog */}
          {selectedUserForReset && <ConfirmDialog open={resetPasswordDialogOpen} title="Reset Password" message={`Are you sure you want to reset the password for ${selectedUserForReset.name}? A new temporary password will be generated and sent to ${selectedUserForReset.email}.`} confirmLabel="Reset Password" showReason={true} reasonLabel="Reason for reset (optional)" reasonPlaceholder="Enter reason for password reset..." onConfirm={confirmResetPassword} onCancel={() => {
          setResetPasswordDialogOpen(false);
          setSelectedUserForReset(null);
        }} isLoading={isResettingPassword} error={resetPasswordError} data-id="element-5064" />}
          <PasswordResetHistoryDrawer open={resetHistoryDrawerOpen} onClose={() => setResetHistoryDrawerOpen(false)} userId={selectedUserForResetHistory?.id} userName={selectedUserForResetHistory?.name} data-id="element-5065" />
        </Container>
      </Box>
    </Box>;
};
export default SubAgentUsers;